import React, { useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  notification,
  Select,
  Spin,
  Typography,
} from "antd";
import { CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const TrackedCardsModal = ({
  visible,
  onCancel,
  trackedCards,
  onDeleteCard,
  onUpdateCard,
  loading,
}) => {
  const [editingCard, setEditingCard] = useState(null);
  const [form] = Form.useForm();

  const handleEditCard = (card) => {
    setEditingCard(card);
    form.setFieldsValue({
      id: card.id,
      name: card.name,
      initial_price: card.initial_price,
      target_price: card.target_price,
      operator: card.operator || "gte",
    });
  };

  const handleUpdateCard = async (values) => {
    await onUpdateCard(values);
    setEditingCard(null);
    form.resetFields();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        notification.success({ message: "Card details copied!" });
      })
      .catch(() => {
        notification.error({ message: "Error copying text." });
      });
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0, textAlign: "center" }}>
          Tracked Cards
        </Title>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      centered
      className="tracked-cards-modal"
    >
      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <List
          dataSource={trackedCards}
          locale={{ emptyText: "You're not tracking any cards yet" }}
          renderItem={(item) => (
            <List.Item className="tracked-card-item" key={item.id}>
              {editingCard && editingCard.id === item.id ? (
                <div className="edit-card-form">
                  <Form
                    form={form}
                    onFinish={handleUpdateCard}
                    layout="vertical"
                    initialValues={{ id: item.id }}
                    className="edit-form"
                  >
                    <Form.Item name="id" hidden>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="name"
                      label="Card Name"
                      rules={[
                        { required: true, message: "Please enter card name" },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      name="operator"
                      label="Price Condition"
                      rules={[
                        {
                          required: true,
                          message: "Please select a condition",
                        },
                      ]}
                    >
                      <Select>
                        <Select.Option value="gte">
                          Greater than or equal
                        </Select.Option>
                        <Select.Option value="lte">
                          Less than or equal
                        </Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="initial_price"
                      label="Initial Price (€)"
                      rules={[
                        {
                          required: true,
                          message: "Please enter initial price",
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="target_price"
                      label="Target Price (€)"
                      rules={[
                        {
                          required: true,
                          message: "Please enter target price",
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item className="form-actions">
                      <Button type="primary" htmlType="submit">
                        Save
                      </Button>
                      <Button onClick={() => setEditingCard(null)}>
                        Cancel
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              ) : (
                <Card
                  className="tracked-card"
                  title={
                    <div className="card-title">
                      <span>{item.name || "Unknown Card"}</span>
                    </div>
                  }
                  actions={[
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        copyToClipboard(
                          `${item.name}: Target price ${item.operator === "gte" ? "≥" : "≤"} ${item.target_price}€, Initial price ${item.initial_price}€`,
                        )
                      }
                      key="copy"
                    >
                      Copy
                    </Button>,
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditCard(item)}
                      key="edit"
                    >
                      Edit
                    </Button>,
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onDeleteCard(item.id)}
                      key="delete"
                    >
                      Delete
                    </Button>,
                  ]}
                >
                  <div className="card-content">
                    <div className="price-info">
                      <Text strong>Target Price:</Text>
                      <Text>
                        {item.operator === "gte" ? "≥" : "≤"}{" "}
                        {item.target_price} €
                      </Text>
                    </div>
                    <div className="price-info">
                      <Text strong>Initial Price:</Text>
                      <Text>{item.initial_price} €</Text>
                    </div>
                  </div>
                </Card>
              )}
            </List.Item>
          )}
          grid={{
            gutter: 16,
            xs: 1,
            sm: 1,
            md: 2,
            lg: 2,
            xl: 2,
            xxl: 2,
          }}
        />
      )}
    </Modal>
  );
};

export default TrackedCardsModal;
